import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import { getAddressByGeocode, getPlaceDetails } from 'components/maps/utils/mapHelpers';

import useAlert from 'hooks/useAlert';
import useClickOutsideSingle from 'hooks/useClickOutsideSingle';
import useDebounce from 'hooks/useDebounce';
import useGoogleAddress from 'hooks/useGoogleAddress';
import useUpdateEffect from 'hooks/useUpdateEffect';

import { decreaseActiveMapsCount, increaseActiveMapsCount } from 'stores/mapsConfigSlice';

import AppLogger from 'utils/AppLogger';

import { faCircleNotch, faLocation, faLocationDot } from '@fortawesome/pro-regular-svg-icons';

import AutoString from './AutoString';
import Button from './Button';
import MapField from './MapField';
import MapModal from './MapModal';
import * as S from './styled/AddressInput.styled';

/**
 * @author @buunsal
 * @description This component is used for address input.
 * @param {Object} props - props of the typography component
 * @param {boolean} [props.required] - If true, the label will be marked as required.
 * @param {boolean} [props.viewMode] - If true, the component will be in view mode.
 * @param {string} [props.name] - Naming so that the input can be called.
 * @param {boolean} [props.disabled] - If true, enters the input disable mode.
 * @param {string} [props.placeholder ] - What should appear in the input's placeholder.
 * @param {Object} [props.selectedCountry] - Selected country object.
 * @returns {object} - Text object with the given props
 * @example
	<AddressInput
		viewMode={formMode !== 'add'}
		label="address"
		errorMessage={errors?.customerAddress?.message}
		required={true}
		disabled={false}
		name="customerAddress"
		placeholder="enterAlocation"
		restrictions={{}}
		showButton={true}
		getPlaceOnFirstLoad={false}
		showCountries={true}
		formElement={true}
	/>
*
 */

const AddressInput = ({ formElement = true, withMap = false, ...rest }) => {
	const { installed, loading } = useSelector((state) => state.mapsConfig);
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(increaseActiveMapsCount());

		return () => {
			dispatch(decreaseActiveMapsCount());
		};
	}, []);

	if (!installed || loading) return <Skeleton width="100%" height="4.5rem" />;
	else if (formElement && withMap) return <FormComponentWithMap {...rest} />;
	else if (formElement) return <FormComponent {...rest} />;
	else return <GeocodeComponent {...rest} />;
};
export default AddressInput;

const FormComponentWithMap = ({
	disabled,
	placeholder = 'enterAlocation',
	name,
	restrictions = null,
	onAddressChange = undefined,
	getPlaceOnFirstLoad = false,
	showCountries = true,
	labelColor = 'var(--black)',
	selectedCountry,
	isGetLatLng,
	className,
	mapProps,
	componentVersion,
	predictionFontSize
}) => {
	const isComponentVersionTwo = componentVersion === 'V2';

	// form methods
	const formMethods = useFormContext();
	if (!formMethods) throw new Error('GeocodeComponent: missing form methods!');

	const { control, setValue } = formMethods;

	const { showError } = useAlert();

	const watchedAddress = useWatch({
		control,
		name
	});

	// watched values
	const text = watchedAddress?.text;

	// Hooks
	const { t } = useTranslation();
	const debouncedText = useDebounce(text, 1000);

	const {
		isLoading,
		predictions,
		getPlaceDataOnFirstLoad,
		getPlaceDetails: getPlaceDetailsMap,
		handleInputChange,
		parseAddress,
		setPredictions
	} = useGoogleAddress({
		name,
		restrictions,
		showCountries,
		setValue,
		onAddressChange,
		watchedAddress,
		useGeocodeParse: true,
		selectedCountry,
		isGetLatLng
	});

	// Effects
	useEffect(() => {
		if (!getPlaceOnFirstLoad) return;
		getPlaceDataOnFirstLoad(text);
	}, [getPlaceOnFirstLoad]);

	useEffect(() => {
		if (!text) {
			setPredictions([]);
		}
	}, [text]);

	useUpdateEffect(() => {
		if (disabled) return;
		setPredictions([]);
		if (!debouncedText) return;
		handleInputChange(debouncedText);
	}, [debouncedText, disabled]);

	const mapRef = useRef(null);

	const handleValueChangeMap = async (value) => {
		try {
			let location;
			if (!value?.lat || !value?.lng) {
				const { lat, lng } = mapRef.current.getCenter();
				location = { lat: lat(), lng: lng() };
			} else location = { lat: value.lat, lng: value.lng };
			const placeDetails = await getPlaceDetails(location);

			if (restrictions?.country?.isNotDifferent) {
				const addressDetails = getAddressByGeocode(placeDetails);

				if (
					addressDetails?.country &&
					addressDetails.country.toLowerCase() !== restrictions?.country?.code?.toLowerCase()
				) {
					showError({
						message: t('errorMsg.countryNotMatch', {
							country: !!restrictions?.country?.code && t(`country.${restrictions.country.code}`)
						})
					});
				} else {
					parseAddress(placeDetails, value?.text ?? placeDetails?.formatted_address);
				}
			} else {
				parseAddress(placeDetails, value?.text ?? placeDetails?.formatted_address);
			}
		} catch (error) {
			AppLogger.error(error);
		}
	};

	return (
		<div className={className}>
			<div style={{ height: '40dvh', width: '100%', marginBottom: '2rem' }}>
				<FormProvider {...formMethods}>
					<MapField
						ref={mapRef}
						value={{
							lat: watchedAddress?.lat ?? null,
							lng: watchedAddress?.lng ?? null
						}}
						setValue={handleValueChangeMap}
						restrictions={restrictions}
						zoom={mapProps?.zoom ?? 17}
						showAutocomplete={mapProps?.showAutocomplete ?? false}
						{...mapProps}
					/>
				</FormProvider>
			</div>
			<FormItem
				label={isComponentVersionTwo ? 'locateAddress' : 'address'}
				isLoading={isLoading}
				color={labelColor}
				style={{
					padding: '1rem 0'
				}}
				{...(isComponentVersionTwo && { required: true })}>
				<S.ButtonContainer>
					<PlaceSearch
						name={`${name}.text`}
						value={text ?? null}
						setValue={(value) =>
							setValue(`${name}.text`, value, {
								shouldValidate: true,
								shouldDirty: true
							})
						}
						setSelectedOption={(obj) => {
							const convertedStr =
								obj?.terms?.length > 1
									? obj.terms.map((term) => term.value).join(', ')
									: obj?.description;

							setValue(`${name}.text`, convertedStr, {
								shouldValidate: true,
								shouldDirty: true
							});

							if (!obj?.place_id) return;
							getPlaceDetailsMap({ ...obj, description: convertedStr });
						}}
						data={predictions}
						disabled={disabled || isLoading}
						placeholder={t(placeholder)}
						predictionFontSize={predictionFontSize}
					/>
					{!disabled && (isLoading || debouncedText !== text) && (
						<S.UtilContainer className="address-utils">
							<Icon icon={faCircleNotch} className="address-loading-icon fa-spin" />
						</S.UtilContainer>
					)}
				</S.ButtonContainer>
			</FormItem>
		</div>
	);
};

const FormComponent = ({
	disabled,
	placeholder = 'enterAlocation',
	name,
	showButton = true,
	restrictions = null,
	onAddressChange = undefined,
	getPlaceOnFirstLoad = false,
	showCountries = true,
	selectedCountry,
	componentVersion,
	variantForAddressGroup,
	restrictCountrySearch,
	predictionFontSize
}) => {
	const isComponentVersionTwo = componentVersion === 'V2';
	const isVariantFullTextAddress = variantForAddressGroup === 'fullTextAddress';
	const isNewFullTextVersion = isComponentVersionTwo && isVariantFullTextAddress;

	const [isInputContainerFocused, setIsInputContainerFocused] = useState(false);

	const inputRef = useRef(null);

	// form methods
	const formMethods = useFormContext();
	if (!formMethods) throw new Error('GeocodeComponent: missing form methods!');

	const { control, setValue } = formMethods;

	const watchedAddress = useWatch({
		control,
		name
	});

	// watched values
	const text = watchedAddress?.text;

	// Hooks
	const { t } = useTranslation();
	const debouncedText = useDebounce(text, 250);

	const {
		isLoading,
		predictions,
		getPlaceDataOnFirstLoad,
		getPlaceDetails,
		handleInputChange,
		handleValueChange,
		parseAddress,
		setPredictions
	} = useGoogleAddress({
		name,
		restrictions,
		showCountries,
		setValue,
		onAddressChange,
		watchedAddress,
		useGeocodeParse: true,
		selectedCountry,
		preserveTextValue: isNewFullTextVersion,
		restrictCountrySearch,
		variantForAddressGroup
	});

	// Effects
	useEffect(() => {
		if (!getPlaceOnFirstLoad) return;
		getPlaceDataOnFirstLoad(text);
	}, [getPlaceOnFirstLoad]);

	useEffect(() => {
		if (!text) {
			setPredictions([]);
		}
	}, [text]);

	useUpdateEffect(() => {
		if (disabled) return;
		setPredictions([]);
		if (!debouncedText) return;
		handleInputChange(debouncedText);
	}, [debouncedText, disabled]);

	const handleClickOutside = () => {
		if (!inputRef.current) return;
		if (!isInputContainerFocused) return;
		if (isNewFullTextVersion && predictions?.length > 0) {
			return setSelectedPrediction(predictions[0]);
		}
	};

	const setSelectedPrediction = async (obj) => {
		const convertedStr = obj?.terms?.length > 1 ? obj.terms.map((term) => term.value).join(', ') : obj?.description;

		!isNewFullTextVersion &&
			setValue(`${name}.text`, convertedStr, {
				shouldValidate: true,
				shouldDirty: true
			});

		if (!obj?.place_id) return;
		await getPlaceDetails({ ...obj, description: convertedStr });
	};

	useClickOutsideSingle(inputRef.current, handleClickOutside);

	return (
		<S.ButtonContainer
			ref={inputRef}
			onFocus={() => setIsInputContainerFocused(true)}
			onBlur={() => setIsInputContainerFocused(false)}>
			<PlaceSearch
				name={`${name}.text`}
				value={text ?? null}
				setValue={(value) =>
					setValue(`${name}.text`, value, {
						shouldValidate: true,
						shouldDirty: true
					})
				}
				setSelectedOption={async (obj) => {
					await setSelectedPrediction(obj);
				}}
				data={isNewFullTextVersion ? [] : predictions}
				disabled={disabled || isLoading}
				placeholder={t(placeholder)}
				predictionFontSize={predictionFontSize}
			/>
			{!disabled && showButton && (
				<Button
					label="fillFromMap"
					icon={faLocation}
					onClick={() => {
						NiceModal.show(MapModal, {
							setValue: handleValueChange,
							value: {
								lat: watchedAddress?.lat ?? null,
								lng: watchedAddress?.lng ?? null
							},
							title: 'selectLocation',
							restrictions,
							formMethods,
							parseAddress
						});
					}}
					loading={isLoading}
				/>
			)}
			{!disabled && (isLoading || debouncedText !== text) && !showButton && (
				<S.UtilContainer className="address-utils">
					<Icon icon={faCircleNotch} className="address-loading-icon fa-spin" />
				</S.UtilContainer>
			)}
		</S.ButtonContainer>
	);
};

const GeocodeComponent = ({
	disabled,
	placeholder = 'enterAlocation',
	value,
	setInputValue,
	restrictions = null,
	showCountries = true,
	onAddressChange = undefined,
	onPlaceChange = undefined,
	variantForAddressGroup,
	predictionFontSize
}) => {
	// Hooks
	const { t } = useTranslation();

	const { isLoading, predictions, getPlaceDetails, handleInputChange, setPredictions } = useGoogleAddress({
		restrictions,
		showCountries,
		onAddressChange,
		useGeocodeParse: false,
		onPlaceChange,
		variantForAddressGroup
	});

	const debouncedText = useDebounce(value, 1000);

	// Effects
	useEffect(() => {
		if (!value) {
			setPredictions([]);
		}
	}, [value]);

	useEffect(() => {
		if (disabled) return;
		if (!debouncedText) return;
		handleInputChange(debouncedText);
	}, [debouncedText, disabled]);

	return (
		<PlaceSearch
			value={value}
			setValue={setInputValue}
			setSelectedOption={(obj) => {
				const convertedStr =
					obj?.terms?.length > 1 ? obj.terms.map((term) => term.value).join(', ') : obj?.description;

				setInputValue(convertedStr);
				if (!obj?.place_id) return;
				getPlaceDetails({ ...obj, description: convertedStr });
			}}
			data={predictions}
			disabled={disabled || isLoading}
			placeholder={t(placeholder)}
			predictionFontSize={predictionFontSize}
		/>
	);
};

const PlaceSearch = (props) => {
	const { predictionFontSize, ...moreProps } = props;

	const columns = useMemo(
		() => [
			{
				accessor: 'terms',
				render: (item, data) => (
					<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
						<Icon icon={faLocationDot} color="var(--black)" fontSize={predictionFontSize} />
						{item.length > 1 ? (
							<div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
								<Typography autoTranslation={false} fontSize={predictionFontSize}>
									{item
										.map((t) => t.value)
										.slice(0, -1)
										.join(', ')
										.concat(',')}
								</Typography>
								<Typography autoTranslation={false} fontWeight={700} fontSize={predictionFontSize}>
									{item.at(-1).value}
								</Typography>
							</div>
						) : (
							<Typography autoTranslation={false} fontSize={predictionFontSize}>
								{data.description}
							</Typography>
						)}
					</div>
				),
				width: '500'
			}
		],
		[]
	);

	return <AutoString columns={columns} autoFilter={false} {...moreProps} />;
};
